import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { Audience, AudiencePerson, AudiencePreview, CampaignItem, CreateCampaignInput, CreateTemplateInput, Paginated, RecipientItem, ResolvedEmails, TemplateItem } from '@crm/shared';
import { resendSyncActive } from '@crm/shared';
import { api } from '../../api/client';

// --- templates -------------------------------------------------------------------

export const useTemplates = () => useQuery({ queryKey: ['templates'], queryFn: async () => (await api.get<TemplateItem[]>('/templates')).data });

function useInvalidate(keys: string[][]) {
  const qc = useQueryClient();
  return () => keys.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
}
export function useCreateTemplate() {
  const inv = useInvalidate([['templates']]);
  return useMutation({ mutationFn: async (input: CreateTemplateInput) => (await api.post<TemplateItem>('/templates', input)).data, onSuccess: inv });
}
export function useUpdateTemplate() {
  const inv = useInvalidate([['templates']]);
  return useMutation({ mutationFn: async ({ id, ...input }: Partial<CreateTemplateInput> & { id: string }) => (await api.patch<TemplateItem>(`/templates/${id}`, input)).data, onSuccess: inv });
}
export function useDeleteTemplate() {
  const inv = useInvalidate([['templates']]);
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/templates/${id}`)).data, onSuccess: inv });
}
export function useDuplicateTemplate() {
  const inv = useInvalidate([['templates']]);
  return useMutation({ mutationFn: async (id: string) => (await api.post<TemplateItem>(`/templates/${id}/duplicate`)).data, onSuccess: inv });
}

// --- campaigns -------------------------------------------------------------------

export const useCampaigns = () => useQuery({ queryKey: ['campaigns'], queryFn: async () => (await api.get<CampaignItem[]>('/campaigns')).data, refetchInterval: 15_000 });
export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: ['campaign', id],
    queryFn: async () => (await api.get<CampaignItem>(`/campaigns/${id}`)).data,
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && (['SENDING', 'SCHEDULED', 'VERIFYING'].includes(q.state.data.status) || resendSyncActive(q.state.data.resendSync)) ? 5_000 : false),
  });
}
export function useRecipients(id: string | undefined, params: { status: string; page: number; pageSize: number; q?: string }, version?: string | number) {
  return useQuery({
    queryKey: ['recipients', id, params, version],
    queryFn: async () => (await api.get<Paginated<RecipientItem>>(`/campaigns/${id}/recipients`, { params })).data,
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}
/** A campaign's "only verified" switches, which change who an audience reaches. */
export type VerifiedRule = { requireVerified: boolean; includeUnconfirmed: boolean };

export function useAudiencePreview(audience: Audience | undefined, rule: VerifiedRule) {
  return useQuery({
    queryKey: ['audience-preview', audience, rule],
    queryFn: async () => (await api.post<AudiencePreview>('/campaigns/audience/preview', { audience, ...rule })).data,
    enabled: !!audience,
    placeholderData: keepPreviousData,
  });
}

/** True while a "Sync from Resend" is queued or running and still reporting (a silent one has stopped). */
export { resendSyncActive };

const KEYS = [['campaigns'], ['campaign'], ['recipients'], ['dashboard']];
export function useCreateCampaign() {
  const inv = useInvalidate(KEYS);
  return useMutation({ mutationFn: async (input: CreateCampaignInput) => (await api.post<CampaignItem>('/campaigns', input)).data, onSuccess: inv });
}
export function useUpdateCampaign() {
  const inv = useInvalidate(KEYS);
  return useMutation({ mutationFn: async ({ id, ...input }: Partial<CreateCampaignInput> & { id: string }) => (await api.patch<CampaignItem>(`/campaigns/${id}`, input)).data, onSuccess: inv });
}
export function useSendCampaign() {
  const inv = useInvalidate(KEYS);
  return useMutation({ mutationFn: async ({ id, scheduledAt }: { id: string; scheduledAt?: string | null }) => (await api.post<CampaignItem>(`/campaigns/${id}/send`, { scheduledAt })).data, onSuccess: inv });
}
export interface SentCopy {
  messageId: string;
  subject: string | null;
  html: string;
  to: string | null;
  sentAt: string;
  status: string;
}
/** A sent email as it went out: one recipient's copy, or the first one sent. */
export function useSentCopy(campaignId: string, recipientId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['campaign-sent-copy', campaignId, recipientId ?? null],
    queryFn: async () => (await api.get<{ copy: SentCopy | null }>(`/campaigns/${campaignId}/sent-copy`, { params: recipientId ? { recipientId } : {} })).data.copy,
    enabled,
    staleTime: 5 * 60_000,
  });
}
export function useResumeCampaign() {
  const inv = useInvalidate(KEYS);
  return useMutation({ mutationFn: async (id: string) => (await api.post<CampaignItem>(`/campaigns/${id}/resume`)).data, onSuccess: inv });
}
export function useSyncCampaignStatus() {
  return useMutation({ mutationFn: async (id: string) => (await api.post<{ queued: boolean }>(`/campaigns/${id}/sync-status`)).data });
}
export function useCancelCampaign() {
  const inv = useInvalidate(KEYS);
  return useMutation({ mutationFn: async (id: string) => (await api.post<CampaignItem>(`/campaigns/${id}/cancel`)).data, onSuccess: inv });
}
export function useTestSend() {
  return useMutation({ mutationFn: async ({ id, to }: { id: string; to: string | string[] }) => (await api.post<{ delivered: boolean; subject: string; sentTo: string[] }>(`/campaigns/${id}/test`, { to })).data });
}

/** One page of who an audience sends to, or leaves off (with the reason). */
export function useAudiencePeople(audience: Audience, rule: VerifiedRule, params: { kind: 'eligible' | 'skipped'; q?: string; page: number; pageSize: number }) {
  return useQuery({
    queryKey: ['audience-people', audience, rule, params],
    queryFn: async () => (await api.post<Paginated<AudiencePerson>>('/campaigns/audience/people', { audience, ...rule, ...params })).data,
    placeholderData: keepPreviousData,
  });
}
export function useResolveEmails() {
  return useMutation({ mutationFn: async (emails: string[]) => (await api.post<ResolvedEmails>('/campaigns/audience/resolve-emails', { emails })).data });
}
/** Uploads a picture or logo for an email design; returns its relative address (/api/files/…). */
export function useUploadEmailImage() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return (await api.post<{ id: string; url: string }>('/files/email-images', form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
    },
  });
}
export function useDuplicateCampaign() {
  const inv = useInvalidate(KEYS);
  return useMutation({ mutationFn: async (id: string) => (await api.post<CampaignItem>(`/campaigns/${id}/duplicate`)).data, onSuccess: inv });
}
export function useDeleteCampaign() {
  const inv = useInvalidate(KEYS);
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/campaigns/${id}`)).data, onSuccess: inv });
}

export const pct = (n: number, d: number) => (d ? `${Math.round((n / d) * 1000) / 10}%` : '—');
